import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ListChecks, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { useCart } from "../../../context/CartContext";
import { api } from "../../../lib/api";
import { useLists } from "../hooks";
import { reorderInto } from "../reorder";
import { SkeletonBlock } from "../../../components/common/EmptyState";
import { useConfirm } from "../../../components/common/ConfirmDialog";

export const ListsCard = ({ shop }) => {
  const { t } = useI18n();
  const lists = useLists();
  const cart = useCart();
  const nav = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();

  const remove = async (l) => {
    if (!(await confirm({ title: `${t("home.deleteList")}: ${l.name}?`, danger: true, confirmLabel: t("home.deleteList") }))) return;
    try {
      await api.delete(`/lists/${l.id}`);
      qc.invalidateQueries({ queryKey: ["lists"] });
      toast.success(t("home.listDeleted"));
    } catch (e) { toast.error(t("common.error")); }
  };

  if (lists.isLoading) return <SkeletonBlock className="h-28" />;
  const rows = lists.data || [];
  return (
    <section data-testid="my-lists">
      <h2 className="text-base font-semibold text-navy md:text-lg">{t("home.myLists")}</h2>
      {rows.length === 0 ? (
        <p className="mt-2 flex items-start gap-2 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-4 text-sm text-slate-500" data-testid="my-lists-empty"><ListChecks className="mt-0.5 h-4 w-4 shrink-0" /> {t("home.noLists")}</p>
      ) : (
        <ul className="mt-2 divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
          {rows.map((l) => (
            <li key={l.id} className="flex items-center gap-2 py-2 pl-4 pr-2" data-testid={`list-row-${l.id}`}>
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-navy">{l.name}</p><p className="text-xs text-slate-500">{t("checkout.itemsCount", { n: l.items.length })}</p></div>
              <button type="button" onClick={() => remove(l)} aria-label={t("home.deleteList")} data-testid={`list-delete-${l.id}`} className="grid h-11 w-11 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              <button type="button" onClick={() => reorderInto(cart, l.items, shop, t) && nav("/app/cart")} data-testid={`list-to-cart-${l.id}`} className="flex h-11 items-center gap-1.5 rounded-full bg-brand px-4 text-sm font-semibold text-white"><ShoppingCart className="h-4 w-4" /> {t("home.listToCart")}</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
